import {
  type ActionEntry,
  type BaseActionEntry,
  type SyncedActionEntry,
  type UpdateActionEntry,
} from '@soulhx/fs-common';
import { DateTime } from 'luxon';
import { v4 as uuid } from 'uuid';
import { db, type ItemSyncStatus, type LocalActionEntry } from '../model/db.js';
import { triggerGlobalSync } from './GlobalSyncMiddleware.js';
import { syncFetch, syncPull } from './syncFetch.js';
import { nextSyncCursor, pullSince, toUtcIso } from './timestamps.js';

export const ACTIONENTRY_SYNC_POLL_INTERVAL_MS = 20_000;
export const ACTIONENTRY_SYNC_KEY = 'actionEntries';

const ACTIONENTRIES_PATH = '/do/e';

// see SettingSync.ts - passing `since` at all (even this far back) routes
// the server to the sync-oriented delta endpoint on the very first pull
const EPOCH = new Date(0).toISOString();

const isUnsynced = (status: ItemSyncStatus | undefined): boolean => status !== undefined && status !== 'synced';

const logSyncError = (context: string, error: unknown) => {
  // eslint-disable-next-line no-console
  console.error(`action entry sync: ${context}`, error);
};

// The local id is sent along so `POST /do/e` is idempotent - a create that
// reached the server but whose response was lost is replayed against the
// same row on the next cycle rather than inserting a duplicate. The server
// echoes that id back; the swap below only matters if it ever doesn't.
const pushCreate = async (local: LocalActionEntry, keepalive?: boolean): Promise<void> => {
  const body: BaseActionEntry = {
    id: local.id,
    actionType: local.actionType,
    completed: local.completed,
    entryDate: local.entryDate,
  };
  const saved = await syncFetch<ActionEntry>({ method: 'POST', path: `${ACTIONENTRIES_PATH}/`, body, keepalive });

  // the row may have changed while the POST was in flight - re-read it
  // rather than overwriting with what was pushed
  const needsAnotherPush = await db.transaction('rw', db.actionEntries, async () => {
    const current = await db.actionEntries.get(local.id);
    await db.actionEntries.delete(local.id);
    const toStore: LocalActionEntry = {
      id: saved.id,
      actionType: saved.actionType,
      completed: saved.completed,
      entryDate: saved.entryDate,
      updateDate: DateTime.now().toISO(),
      syncStatus: 'synced',
    };

    if (!current) {
      // deleted locally (e.g. an "Undo") - queueLocalActionEntryDelete just
      // drops pendingCreate rows, but this one has now reached the server,
      // so it has to be deleted there too
      await db.actionEntries.put({ ...toStore, syncStatus: 'pendingDelete' });
      return true;
    }
    if (current.updateDate !== local.updateDate) {
      // edited locally - keep the edits and push them as an update
      await db.actionEntries.put({
        ...toStore,
        entryDate: current.entryDate,
        completed: current.completed,
        syncStatus: 'pendingUpdate',
      });
      return true;
    }

    await db.actionEntries.put(toStore);
    return false;
  });

  if (needsAnotherPush) triggerGlobalSync(ACTIONENTRY_SYNC_KEY);
};

const pushUpdate = async (local: LocalActionEntry, keepalive?: boolean): Promise<void> => {
  const body: UpdateActionEntry = { id: local.id, entryDate: local.entryDate };
  const saved = await syncFetch<ActionEntry>({
    method: 'PUT',
    path: `${ACTIONENTRIES_PATH}/${local.id}`,
    body,
    keepalive,
  });

  await db.transaction('rw', db.actionEntries, async () => {
    // only mark synced if nothing changed while the PUT was in flight - a
    // newer edit (or a delete) is left pending for the next cycle
    const current = await db.actionEntries.get(local.id);
    if (current?.syncStatus !== 'pendingUpdate' || current.updateDate !== local.updateDate) return;

    await db.actionEntries.update(local.id, {
      entryDate: saved.entryDate,
      updateDate: DateTime.now().toISO(),
      syncStatus: 'synced',
    });
  });
};

const pushDelete = async (local: LocalActionEntry, keepalive?: boolean): Promise<void> => {
  await syncFetch<{ success: boolean }>({ method: 'DELETE', path: `${ACTIONENTRIES_PATH}/${local.id}`, keepalive });
  await db.actionEntries.delete(local.id);
};

const runPush = async (keepalive?: boolean): Promise<void> => {
  const pending = await db.actionEntries.filter((e) => isUnsynced(e.syncStatus)).toArray();

  for (const local of pending) {
    try {
      switch (local.syncStatus ?? 'pendingCreate') {
        case 'pendingCreate':
          await pushCreate(local, keepalive);
          break;
        case 'pendingUpdate':
          await pushUpdate(local, keepalive);
          break;
        case 'pendingDelete':
          await pushDelete(local, keepalive);
          break;
      }
    } catch (error) {
      // leave the row's syncStatus untouched so it's retried on the next cycle
      logSyncError(`failed to push action entry ${local.id}`, error);
    }
  }
};

// Syncs are kicked off independently by the debounced trigger, the poll
// interval and the unload flush, so pushes and pulls are all chained - a
// later run only starts once the previous one has settled. Two overlapping
// pushes would both see the same `pendingCreate` row and both POST it; and
// a pull fetched before a push's DELETE landed would write the deleted row
// back once the push had removed it locally.
let syncChain: Promise<void> = Promise.resolve();

const enqueue = (fn: () => Promise<void>): Promise<void> => {
  const run = syncChain.then(fn);
  syncChain = run.catch(() => {});
  return run;
};

export const pushPendingActionEntries = (options?: { keepalive?: boolean }): Promise<void> =>
  enqueue(() => runPush(options?.keepalive));

const runPull = async (): Promise<void> => {
  const cursor = await db.syncMeta.get(ACTIONENTRY_SYNC_KEY);
  const since = cursor?.lastSyncedAt ?? EPOCH;
  const { data: serverEntries, horizon } = await syncPull<SyncedActionEntry[]>(
    `${ACTIONENTRIES_PATH}/sync?since=${encodeURIComponent(pullSince(since))}`
  );

  let maxLastUpdate = toUtcIso(since);

  await db.transaction('rw', db.actionEntries, db.syncMeta, async () => {
    for (const serverEntry of serverEntries) {
      // compared in UTC: the API emits its own zone, and the stored cursor
      // may be a (UTC) sync horizon - see nextSyncCursor
      const updateDate = toUtcIso(serverEntry.updateDate);
      if (updateDate > maxLastUpdate) {
        maxLastUpdate = updateDate;
      }

      if (serverEntry.deleted) {
        await db.actionEntries.delete(serverEntry.id);
        continue;
      }

      const local = await db.actionEntries.get(serverEntry.id);
      if (local && isUnsynced(local.syncStatus)) continue;

      if (local?.updateDate === serverEntry.updateDate) continue;

      const toStore: LocalActionEntry = {
        id: serverEntry.id,
        actionType: serverEntry.actionType,
        completed: serverEntry.completed,
        entryDate: serverEntry.entryDate,
        updateDate: serverEntry.updateDate,
        syncStatus: 'synced',
      };
      await db.actionEntries.put(toStore);
    }

    await db.syncMeta.put({
      itemId: ACTIONENTRY_SYNC_KEY,
      lastSyncedAt: nextSyncCursor(since, maxLastUpdate, horizon),
    });
  });
};

export const pullActionEntries = (): Promise<void> => enqueue(runPull);

/**
 * Pushes any pending local action-entry mutations, then pulls whatever
 * changed on the server since the last sync. Push-before-pull so local
 * changes reach `synced` before we ask "what's new", minimizing cases
 * where the pull side has to skip an in-flight local row.
 */
export const syncActionEntries = (): Promise<void> =>
  enqueue(async () => {
    await runPush();
    await runPull();
  });

export const queueLocalActionEntryCreate = async (
  actionType: string,
  completed: boolean,
  entryDate?: string
): Promise<string> => {
  const localEntry: LocalActionEntry = {
    id: uuid(),
    actionType,
    completed,
    entryDate: entryDate ?? DateTime.now().toISO(),
    updateDate: DateTime.now().toISO(),
    syncStatus: 'pendingCreate',
  };

  await db.actionEntries.add(localEntry);
  triggerGlobalSync(ACTIONENTRY_SYNC_KEY);
  return localEntry.id;
};

export const queueLocalActionEntryUpdate = async (
  id: string,
  changes: { entryDate?: string; completed?: boolean }
): Promise<void> => {
  const updated = await db.transaction('rw', db.actionEntries, async () => {
    const existing = await db.actionEntries.get(id);
    if (!existing || existing.syncStatus === 'pendingDelete') return false;

    const nextStatus: ItemSyncStatus = existing.syncStatus === 'pendingCreate' ? 'pendingCreate' : 'pendingUpdate';

    await db.actionEntries.update(id, {
      entryDate: changes.entryDate ?? existing.entryDate,
      updateDate: DateTime.now().toISO(),
      completed: changes.completed ?? existing.completed,
      syncStatus: nextStatus,
    });
    return true;
  });

  if (updated) triggerGlobalSync(ACTIONENTRY_SYNC_KEY);
};

export const queueLocalActionEntryDelete = async (id: string): Promise<void> => {
  const shouldSync = await db.transaction('rw', db.actionEntries, async () => {
    const existing = await db.actionEntries.get(id);
    if (!existing) return false;

    if (existing.syncStatus === undefined || existing.syncStatus === 'pendingCreate') {
      // never reached the server - just vanish, no network call
      await db.actionEntries.delete(id);
      return false;
    }

    if (existing.syncStatus === 'pendingDelete') return false;

    await db.actionEntries.update(id, { syncStatus: 'pendingDelete' });
    return true;
  });

  if (shouldSync) triggerGlobalSync(ACTIONENTRY_SYNC_KEY);
};
